/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';

import { useAINudgesRefreshed } from '@atlassian/ai-smart-button-api';
import { type RovoExperienceSidebarAPI } from '@atlassian/rovo-experience-api';

import { useNudgeSessionState } from './use-nudge-session-state';

// Lazy load to avoid loading useRovoActionTrigger when there's no active nudge
const LazyAIProactiveNudgeRefreshed = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-proactive-nudge-refreshed" */ './main').then(
		(module) => ({
			default: module.AIProactiveNudgeRefreshed,
		}),
	),
);

type AIProactiveNudgeProps = {
	sidebarApi: RovoExperienceSidebarAPI;
};

export const AIProactiveNudgeRefreshed = ({ sidebarApi }: AIProactiveNudgeProps) => {
	const { acceptedNudges, dismissedNudges, addAcceptedNudge, addDismissedNudge } =
		useNudgeSessionState();
	const selectedNudges = useAINudgesRefreshed();

	const potentialActiveNudge = useMemo(() => {
		/**
		 * ConversationAction must not be previously dismissed or accepted
		 * to be considered active.
		 */
		return selectedNudges?.find(
			(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
		);
	}, [selectedNudges, dismissedNudges, acceptedNudges]);

	// Only render the lazy component if there's a potential nudge to show
	if (!potentialActiveNudge) {
		return null;
	}

	return (
		<React.Suspense fallback={null}>
			<LazyAIProactiveNudgeRefreshed
				sidebarApi={sidebarApi}
				potentialActiveNudge={potentialActiveNudge}
				addAcceptedNudge={addAcceptedNudge}
				addDismissedNudge={addDismissedNudge}
			/>
		</React.Suspense>
	);
};
